<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import clone from 'lodash/clone'
import { isLast } from '~/classes/Utils'

import type {
  BusinessHours,
  FormattedTimeInterval, TimeInterval,
} from '~/definitions/AllDefinitions'

const props = defineProps<{
  hours: BusinessHours
}>()
const { t } = useI18n()
const formattedHours = computed(() => {
  return convertHoursToIntervals(props.hours)
})

/**
 * Author Name: konijn
 * Author URL: https://codereview.stackexchange.com/users/14625/konijn
 * URL: https://codereview.stackexchange.com/questions/286384/list-consecutive-days#answer-286437
 * @param days
 */
function formatDays(days: string[]) {
  const orderedDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as string[]
  const formatDateRange = (start: number, end: number) => start === end
    ? t(orderedDays[start])
    : `${t(orderedDays[start])} - ${t(orderedDays[end])}`
  const numberedDays = days.map(day => orderedDays.indexOf(day)).sort()
  const groupedDays = []
  let start = 0
  let end = 0
  for (let i = 0; i < numberedDays.length; i++) {
    const day = numberedDays[i]
    if (i === 0) {
      start = end = day
      continue
    }
    if (day === (end + 1)) {
      end = day
    } else {
      groupedDays.push(formatDateRange(start, end))
      start = end = day
    }
  }
  groupedDays.push(formatDateRange(start, end))
  return groupedDays.join(', ')
}
function convertHoursToIntervals(businessHours: BusinessHours) {
  const ret = {
    twentyFourHours: [] as string[],
    intervals: [] as FormattedTimeInterval[],
  }
  const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
  const closed = [] as string[]
  const allIntervals = {} as any
  days.forEach((day) => {
    let tempInterval = ''
    let hours
    if (businessHours[day]) {
      switch (businessHours[day].type) {
        case 'twenty_four_hours':
          if (!ret.twentyFourHours.includes(day)) {
            ret.twentyFourHours.push(day)
          }
          break
        case 'time_interval':
          if (!closed.includes(day)) {
            hours = cloneDeep(businessHours[day].formattedHours)
            hours.forEach((hour: TimeInterval) => {
              tempInterval = hour.from.replace(':', '') + hour.to.replace(':', '')
              if (typeof allIntervals[tempInterval] === 'undefined') {
                allIntervals[tempInterval] = {
                  from: clone(hour.from),
                  to: clone(hour.to),
                  days: [],
                  formattedDays: '',
                } as FormattedTimeInterval
              }
              allIntervals[tempInterval].days.push(day)
            })
          }
          break
      }
    }
  })
  if (Object.keys(allIntervals).length) {
    for (const interval in allIntervals) {
      const newInterval = allIntervals[interval]
      newInterval.formattedDays = formatDays(newInterval.days)
      ret.intervals.push(newInterval)
    }
  }
  return ret
}
</script>

<template>
  <div class="w-full">
    <div
      v-if="formattedHours.intervals.length || formattedHours.twentyFourHours.length"
      class="flex flex-row flex-wrap w-full"
    >
      <div class="mr-1 font-semibold">
        {{ t('AVAILABLE') }}
      </div>
      <div v-if="formattedHours.intervals.length">
        <div
          v-for="(interval, intervalIdx) in formattedHours.intervals"
          :key="intervalIdx"
        >
          <span class="font-semibold">{{ interval.formattedDays }}</span>
          <span class="">
            {{ `: ${interval.from} - ${interval.to}` }}
          </span>
        </div>
      </div>
      <div
        v-if="formattedHours.twentyFourHours.length"
        :class="{ 'w-full': formattedHours.intervals.length }"
      >
        <span
          v-for="(day, dayIdx) in formattedHours.twentyFourHours"
          :key="day"
        >
          {{ t(day) }}<span v-if="!isLast(formattedHours.twentyFourHours, +dayIdx)">, </span>
        </span>
        <span>
          &nbsp;{{ t('TWENTY_FOUR_HOURS') }}
        </span>
      </div>
    </div>
  </div>
</template>
